import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';

/**
 * Cloudflare Turnstile bot challenge.
 *
 * The SITE key is fetched from the `security-config` edge function (the secret
 * key never reaches the browser). The token produced here is ALWAYS re-verified
 * server-side (auth-guard / crm-capture) — the widget alone is not proof.
 *
 * REGISTRATION FIX (2026-09-24): tokens are single-use; parents bump `resetKey`
 * after every server check so a retry always gets a fresh token.
 *
 * LOGIN INCIDENT FIX (2026-09-24, "The check hit a problem and will retry
 * automatically"): the error-callback used to DISCARD Cloudflare's error code,
 * so nobody could tell a domain/sitekey misconfiguration (110200 / 1101xx /
 * 400020 — never recovers) from a transient challenge failure (300xxx /
 * 600xxx). With `retry: 'auto'` a config error looped forever. Now:
 *   - the code is captured, shown in small print, and reported once per page
 *     load to mkt_security_events (event_type turnstile_client_error) with the
 *     hostname, so Admin > Security shows exactly what Cloudflare returned
 *   - config-class codes stop retrying immediately; transient codes stop after
 *     3 consecutive errors so the widget never spins endlessly
 *   - 200500 (iframe could not load) is treated as 'blocked' (ad blocker / VPN)
 */
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

export type TurnstileStatus = 'loading' | 'ready' | 'expired' | 'error' | 'blocked' | 'disabled';

let cachedSiteKey: string | null | undefined;
let scriptPromise: Promise<boolean> | null = null;
const reported = new Set<string>();

export const loadTurnstileConfig = async (): Promise<string | null> => {
  if (cachedSiteKey !== undefined) return cachedSiteKey ?? null;
  try {
    const { data } = await supabase.functions.invoke('security-config', { body: {} });
    cachedSiteKey = data?.turnstile?.siteKey || null;
  } catch {
    cachedSiteKey = null;
  }
  return cachedSiteKey ?? null;
};

/** Resolves true when the Cloudflare script loaded, false when it was blocked. */
const loadScript = () => {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<boolean>((resolve) => {
    if (window.turnstile) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(!!window.turnstile);
    s.onerror = () => {
      scriptPromise = null;
      resolve(false);
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
};

/** Cloudflare client error families (developers.cloudflare.com/turnstile/troubleshooting/client-side-errors). */
export const classifyTurnstileError = (code: string): 'config' | 'blocked' | 'transient' => {
  const c = String(code || '');
  if (/^1101\d\d$/.test(c) || c === '110200' || c === '400020' || /^4000\d\d$/.test(c)) return 'config';
  if (c === '200500' || c === '200100') return 'blocked';
  return 'transient';
};

const reportClientError = (code: string, action: string, kind: string) => {
  const key = `${action}:${code}`;
  if (reported.has(key)) return;
  reported.add(key);
  supabase
    .from('mkt_security_events')
    .insert({
      event_type: 'turnstile_client_error',
      severity: kind === 'config' ? 'high' : 'warning',
      outcome: 'widget_error',
      path: typeof window !== 'undefined' ? window.location.pathname : null,
      detail: `Turnstile client error ${code || 'unknown'} on ${action}`,
      metadata: {
        code: code || 'unknown',
        class: kind,
        action,
        hostname: typeof window !== 'undefined' ? window.location.hostname : '',
        ua: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 160) : '',
      },
    })
    .then(() => undefined, () => undefined);
};

interface Props {
  action: string;
  onVerify: (token: string) => void;
  onStatus?: (status: TurnstileStatus) => void;
  /** Change this value to discard the current (spent) token and get a new one. */
  resetKey?: number;
  className?: string;
  label?: string;
}

const MAX_TRANSIENT_ERRORS = 3;

const Turnstile: React.FC<Props> = ({ action, onVerify, onStatus, resetKey = 0, className = '', label }) => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const ref = useRef<HTMLDivElement | null>(null);
  const widget = useRef<string | null>(null);
  const errorCount = useRef(0);
  const [status, setStatusState] = useState<TurnstileStatus>('loading');
  const [errorCode, setErrorCode] = useState('');
  const [attempt, setAttempt] = useState(0);

  const setStatus = (s: TurnstileStatus) => {
    setStatusState(s);
    onStatus?.(s);
  };

  const manualRetry = () => {
    errorCount.current = 0;
    setErrorCode('');
    setAttempt((a) => a + 1);
  };

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    (async () => {
      const siteKey = await loadTurnstileConfig();
      if (cancelled) return;
      if (!siteKey) {
        setStatus('disabled');
        onVerify('');
        return;
      }
      const loaded = await loadScript();
      if (cancelled) return;
      if (!loaded || !ref.current || !window.turnstile) {
        setStatus('blocked');
        onVerify('');
        reportClientError('script_blocked', action, 'blocked');
        return;
      }
      try {
        widget.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          action: action.slice(0, 32),
          theme: 'light',
          'refresh-expired': 'auto',
          // We manage retries ourselves so a config error cannot loop forever.
          retry: 'never',
          callback: (token: string) => {
            errorCount.current = 0;
            setErrorCode('');
            setStatus('ready');
            onVerify(token);
          },
          'expired-callback': () => {
            setStatus('expired');
            onVerify('');
          },
          'error-callback': (code?: string | number) => {
            const c = String(code ?? '');
            const kind = classifyTurnstileError(c);
            errorCount.current += 1;
            setErrorCode(c || 'unknown');
            onVerify('');
            reportClientError(c, action, kind);
            if (kind === 'blocked') {
              setStatus('blocked');
            } else {
              setStatus('error');
              // Transient: retry a few times with back-off, then stop and ask.
              if (kind === 'transient' && errorCount.current < MAX_TRANSIENT_ERRORS) {
                window.setTimeout(() => {
                  try {
                    if (widget.current && window.turnstile) {
                      window.turnstile.reset(widget.current);
                      setStatus('loading');
                    }
                  } catch {
                    /* ignore */
                  }
                }, 1500 * errorCount.current);
              }
            }
            return true; // handled — suppress Cloudflare's console throw
          },
        });
      } catch {
        setStatus('error');
        setErrorCode('render_failed');
        onVerify('');
        reportClientError('render_failed', action, 'config');
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      } catch {
        /* ignore */
      }
      widget.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, attempt]);

  // Parent asked for a fresh token (the previous one was consumed server-side).
  useEffect(() => {
    if (!resetKey) return;
    onVerify('');
    errorCount.current = 0;
    if (widget.current && window.turnstile) {
      try {
        window.turnstile.reset(widget.current);
        setStatus('loading');
        return;
      } catch {
        /* fall through to a full re-render */
      }
    }
    setAttempt((a) => a + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (status === 'disabled') return null;

  const kind = errorCode ? classifyTurnstileError(errorCode) : 'transient';
  const willAutoRetry = kind === 'transient' && errorCount.current < MAX_TRANSIENT_ERRORS;

  return (
    <div className={className}>
      <div ref={ref} />
      {status === 'blocked' && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="flex items-center gap-1.5 font-semibold">
            <AlertTriangle className="h-4 w-4" />
            {es ? 'No se pudo cargar la verificación de seguridad.' : 'The security check could not load.'}
          </p>
          <p className="mt-1">
            {es
              ? 'Un bloqueador de anuncios, VPN o navegador privado puede estar bloqueando challenges.cloudflare.com. Desactívelo para este sitio o use otro navegador y vuelva a intentarlo.'
              : 'An ad blocker, VPN or privacy browser may be blocking challenges.cloudflare.com. Allow it for this site or try another browser, then retry.'}
          </p>
          <button
            type="button"
            onClick={manualRetry}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-bold text-amber-900 hover:bg-amber-100"
          >
            <RefreshCw className="h-3.5 w-3.5" /> {es ? 'Reintentar' : 'Retry'}
          </button>
        </div>
      )}
      {status === 'error' && (
        <div className="mt-1.5 text-xs text-amber-700">
          <p className="flex flex-wrap items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            {willAutoRetry
              ? es
                ? 'La verificación tuvo un problema. Se reintentará automáticamente.'
                : 'The check hit a problem and will retry automatically.'
              : es
              ? 'La verificación de seguridad no pudo completarse.'
              : 'The security check could not complete.'}
            <button type="button" onClick={manualRetry} className="font-bold underline">
              {es ? 'Reintentar ahora' : 'Retry now'}
            </button>
          </p>
          {errorCode && (
            <p className="mt-0.5 text-[11px] text-slate-400">
              {es ? 'Código' : 'Code'}: {errorCode}
            </p>
          )}
        </div>
      )}
      {label && status !== 'blocked' && (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
          <ShieldCheck className={`h-3.5 w-3.5 ${status === 'ready' ? 'text-emerald-500' : 'text-slate-400'}`} />
          {label}
        </p>
      )}
    </div>
  );
};

export default Turnstile;
